const BASE64_PAD_LENGTH = 4;
const MIN_JWT_SEGMENTS = 2;

// JWT segments are base64url-encoded (`-`/`_` instead of `+`/`/`, no padding).
// `atob` only accepts standard base64 and throws on `-`/`_`, so normalise
// first: swap the alphabet and re-pad to a multiple of 4.
function base64UrlToBase64(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = base64.length % BASE64_PAD_LENGTH;
  return remainder === 0 ? base64 : base64.padEnd(base64.length + (BASE64_PAD_LENGTH - remainder), "=");
}

/**
 * Decodes the unverified payload of a JWT for client-side use only. NOT
 * signature-verified — the backend is the authority on the bearer. This only
 * proves the value is a structurally decodable JWT whose payload is a
 * non-null, non-array object.
 *
 * Returns `null` instead of throwing so a malformed `?access_token=x` cannot
 * tear down the caller. Decoding the bytes through `TextDecoder` (rather than
 * `atob` alone) keeps non-ASCII claims intact instead of mangling them.
 */
export default function decodeJwtClaims(jwt: string): Record<string, unknown> | null {
  try {
    const tokenComponents = jwt.split(".");
    if (tokenComponents.length < MIN_JWT_SEGMENTS) {
      return null;
    }
    const bytes = Uint8Array.from(atob(base64UrlToBase64(tokenComponents[1])), (character) => character.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof claims !== "object" || claims === null || Array.isArray(claims)) {
      return null;
    }
    return claims as Record<string, unknown>;
  } catch {
    return null;
  }
}
